import {
  Backdrop,
  Box,
  Button,
  Modal,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import theme from '../../theme';
import LoadingPage from '../LoadingPage';
import { ProductContext } from '../../context/ProductContext';
import { useNavigate } from 'react-router-dom';
import ProductList from './ProductList';
import { useAuth } from '../../hooks/useAuth';

const Products = () => {
  const [productLink, setProductLink] = useState<string>('');
  const [showProcessingModal, setShowProcessingModal] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<boolean>(false);

  const { authUser } = useAuth();
  const { setProductInfo, setAllProducts, handleExtractProduct, loadProducts } =
    useContext(ProductContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (authUser?.uid) {
      setProductInfo(null);
      setAllProducts(null);
      loadProducts(authUser.uid, true);
    }
  }, [authUser?.uid, loadProducts]);

  const handleCloseModal = () => {
    setShowProcessingModal(false);
  };

  const normalizeUrl = (raw: string) => {
    const s = raw.trim();
    if (!s) return s;
    // if no scheme, assume https
    if (!/^[a-zA-Z][\w+.-]*:/.test(s)) return `https://${s}`;
    return s;
  };

  const validateUrl = (raw: string): string | null => {
    const s = raw.trim();
    if (!s) return 'Please enter a URL';
    try {
      const normalized = normalizeUrl(s);
      const u = new URL(normalized);
      if (!/^https?:$/.test(u.protocol))
        return 'URL must start with http or https';
      if (!u.hostname || !u.hostname.includes('.'))
        return 'Enter a valid domain';
      return null;
    } catch {
      return 'Enter a valid URL';
    }
  };

  const urlError = useMemo(
    () => (touched ? validateUrl(productLink) : null),
    [touched, productLink]
  );

  const handleAddProduct = async () => {
    const vErr = validateUrl(productLink);
    if (vErr) {
      setTouched(true);
      setError(vErr);
      return;
    }

    const normalized = normalizeUrl(productLink);
    setError(null);
    setShowProcessingModal(true);

    try {
      // Extract product details only - Magic Link will be generated after publishing
      await handleExtractProduct(authUser?.uid ?? '', normalized);

      setShowProcessingModal(false);
      setProductLink('');
      setTouched(false);

      // Navigate to edit page
      navigate('edit');
    } catch (e: any) {
      setShowProcessingModal(false);
      setError(e?.message || 'Failed to process link.');
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    margin: 'auto',
    maxWidth: '500px',
    minWidth: '400px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '10px',
    p: 4,
  };

  return (
    <Box>
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant='h6'>Products</Typography>
      </Box>
      <Box
        sx={{
          p: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 2,
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              pb: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'start',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: '20px',
                  height: '20px',
                  bgcolor: theme.palette.primary.main,
                  borderRadius: 2,
                }}
              ></Box>
              <Typography variant='h7'>Add Product</Typography>
            </Box>
            <Typography variant='body2'>
              Your AI persona can recommend relevant products during
              conversations. A trackable Magic Link will be automatically
              generated when you publish the product.
            </Typography>
          </Box>
          <Box
            sx={{
              py: 2,
            }}
          >
            <Typography variant='h7'>Link URL</Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                gap: 1,
                mt: 2,
              }}
            >
              <TextField
                fullWidth
                placeholder='https://…'
                value={productLink}
                onChange={(e) => {
                  setProductLink(e.target.value);
                  if (error) setError(null);
                }}
                onBlur={() => setTouched(true)}
                error={Boolean(urlError || error)}
                helperText={urlError || error || ' '}
                type='url'
                slotProps={{
                  input: {
                    sx: {
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? theme.palette.background.paper
                          : 'common.white',
                      color: 'text.primary',
                      height: '100%',
                      borderRadius: 2,
                      '& fieldset': {
                        border: '2px solid #42424250',
                      },
                    },
                  },
                }}
                variant='outlined'
                sx={{
                  height: '40px',
                  flex: 1,
                  '& .MuiInputBase-root': {
                    height: '100%',
                  },
                }}
              />
              <Button
                variant='contained'
                disabled={!productLink}
                sx={{
                  height: { xs: 40, md: '-webkit-fill-available' },
                  width: { xs: '100%', md: '100px' },
                  bgcolor: theme.palette.primary.main,
                }}
                onClick={handleAddProduct}
              >
                + Add
              </Button>
            </Box>
          </Box>
        </Paper>
        <Box>
          <ProductList />
        </Box>
      </Box>

      {/* Processing Modal */}
      <Modal
        open={showProcessingModal}
        onClose={handleCloseModal}
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <LoadingPage />
            <Typography variant='h7'>Processing Link...</Typography>
            <Typography variant='body2'>
              Please wait, fetching product details...
            </Typography>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Products;
